import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useToast } from '../../components/Toast';
import useUrlState from '../../hooks/useUrlState';
import { useAuth } from '../../auth/AuthProvider';
import Header from '../../components/Header';
import { colors } from '../../styles/theme';
import { searchMatchAny } from '../../utils/searchMatch';
import { logError } from '../../utils/logError';
import SearchableSelect from '../../components/SearchableSelect';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

// ══════════════════════════════════════════════════════
// ANA SAYFA
// ══════════════════════════════════════════════════════
function EslestirmeMerkeziPage() {
  useAuth();
  const [activeTab, setActiveTab] = useUrlState('tab', 'LOGO_CARI');
  const [ozet, setOzet] = useState([]);
  const toast = useToast();

  // Referans veriler
  const [tedarikciler, setTedarikciler] = useState([]);
  const [malzemeler, setMalzemeler] = useState([]);
  const [receteler, setReceteler] = useState([]);

  // Sonuç bildirimi: sağ üst köşe toast (sayfa-içi banner kaldırıldı — scroll
  // aşağıdayken görünmüyordu; toast her zaman görünür).
  const showMsg = (msg, type = 'success') => {
    (toast[type] || toast.info)(msg);
  };

  const fetchOzet = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/entegrasyon/ozet`, { headers: getAuthHeaders() });
      if (res.ok) setOzet(await res.json());
    } catch (e) { logError('Özet hatası:', e); }
  }, []);

  const fetchReferanslar = useCallback(async () => {
    try {
      const [tRes, mRes, rRes] = await Promise.all([
        fetch(`${API}/api/tedarikciler`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/malzemeler`, { headers: getAuthHeaders() }),
        fetch(`${API}/api/recipes`, { headers: getAuthHeaders() }),
      ]);
      if (tRes.ok) setTedarikciler(await tRes.json());
      if (mRes.ok) setMalzemeler(await mRes.json());
      if (rRes.ok) {
        const data = await rRes.json();
        setReceteler(Array.isArray(data) ? data : data.data || []);
      }
    } catch (e) { logError('Referans hatası:', e); }
  }, []);

  useEffect(() => { fetchOzet(); fetchReferanslar(); }, [fetchOzet, fetchReferanslar]);

  const tabConfig = [
    { key: 'LOGO_CARI', label: 'Logo Cari', icon: '🏢' },
    { key: 'LOGO_URUN', label: 'Logo Ürün', icon: '📦' },
    { key: 'MODPOS_URUN', label: 'ModPOS Ürün', icon: '🖥️' },
  ];

  // KPI
  const ozetMap = {};
  ozet.forEach(o => { ozetMap[`${o.dis_sistem}_${o.dis_varlik_tipi}`] = o; });
  const toplamEslesmis = ozet.reduce((s, o) => s + o.eslesmis, 0);
  const toplamEslesmemis = ozet.reduce((s, o) => s + o.eslesmemis, 0);
  const toplamKayit = ozet.reduce((s, o) => s + o.toplam, 0);

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1400, margin: '0 auto' }}>
      <Header title="Eşleştirme Merkezi" subtitle="Tüm dış sistem eşleştirmelerini tek sayfadan yönetin — Logo İşbaşı, ModPOS" />


      {/* KPI */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
        <KpiCard label="Toplam Kayıt" value={toplamKayit} icon="📊" color="#6c5ce7" />
        <KpiCard label="Eşleşmiş" value={toplamEslesmis} icon="✅" color="#27ae60" />
        <KpiCard label="Eşleşmemiş" value={toplamEslesmemis} icon="⚠️" color="#f39c12" />
        {tabConfig.map(t => {
          const mapKey = t.key === 'LOGO_CARI' ? 'LOGO_CARI' : t.key === 'LOGO_URUN' ? 'LOGO_URUN' : 'MODPOS_URUN_ADI';
          const oData = ozetMap[mapKey];
          return <KpiCard key={t.key} label={t.label} value={oData ? `${oData.eslesmis}/${oData.toplam}` : '0/0'} icon={t.icon} color="#3498db" />;
        })}
      </div>

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #2c3e50' }}>
        {tabConfig.map(t => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '10px 24px', border: 'none', cursor: 'pointer', fontSize: 13,
              borderBottom: activeTab === t.key ? '3px solid #2c3e50' : '3px solid transparent',
              background: activeTab === t.key ? '#fff' : '#f8f9fa',
              color: activeTab === t.key ? '#2c3e50' : '#666',
              fontWeight: activeTab === t.key ? 700 : 400,
              transition: 'all 0.15s',
            }}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Tab İçerik */}
      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderTop: 'none', borderRadius: '0 0 8px 8px', padding: 20 }}>
        {activeTab === 'LOGO_CARI' && (
          <LogoCariTab tedarikciler={tedarikciler} onRefresh={fetchOzet} showMsg={showMsg} />
        )}
        {activeTab === 'LOGO_URUN' && (
          <LogoUrunTab malzemeler={malzemeler} onRefresh={fetchOzet} showMsg={showMsg} />
        )}
        {activeTab === 'MODPOS_URUN' && (
          <ModPosTab receteler={receteler} malzemeler={malzemeler} onRefresh={fetchOzet} showMsg={showMsg} />
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// LOGO CARİ TAB
// ══════════════════════════════════════════════════════
function LogoCariTab({ tedarikciler, onRefresh, showMsg }) {
  const [eslestirmeler, setEslestirmeler] = useState([]);
  const [cariler, setCariler] = useState([]);
  const [cariLoading, setCariLoading] = useState(false);
  const [eslLoading, setEslLoading] = useState(false);
  const [logoConnected, setLogoConnected] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('eslesmemis');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedTedarikci, setSelectedTedarikci] = useState({});
  const pageSize = 50;

  // Logo bağlantı durumu
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/logo/status`, { headers: getAuthHeaders() });
        if (res.ok) { const d = await res.json(); setLogoConnected(d.connected); }
        else setLogoConnected(false);
      } catch { setLogoConnected(false); }
    })();
  }, []);

  // Eşleştirmeler
  const fetchEslestirmeler = useCallback(async () => {
    setEslLoading(true);
    try {
      const res = await fetch(`${API}/api/logo/eslestirmeler`, { headers: getAuthHeaders() });
      if (res.ok) setEslestirmeler(await res.json());
    } catch (e) { logError(e); }
    setEslLoading(false);
  }, []);

  useEffect(() => { fetchEslestirmeler(); }, [fetchEslestirmeler]);

  // Logo SATIŞ faturalarındaki cariler
  // GİDEN liste cevabında müşteri ünvanı `supplier` alanında geliyor.
  // Müşteri VKN'si bu listede gelmediği için ünvan bazlı benzersiz liste gösteriyoruz.
  const fetchCariler = useCallback(async () => {
    if (!logoConnected) return;
    setCariLoading(true);
    try {
      const now = new Date();
      const baslangic = `${now.getFullYear()}-01-01`;
      const bitis = now.toISOString().slice(0, 10);
      const pageSizeLogo = 500;
      const faturalar = [];

      for (let logoPage = 1; logoPage <= 50; logoPage += 1) {
        const res = await fetch(
          `${API}/api/logo/faturalar/giden?baslangic=${baslangic}&bitis=${bitis}&page=${logoPage}&page_size=${pageSizeLogo}`,
          { headers: getAuthHeaders() }
        );

        if (!res.ok) break;

        const data = await res.json();
        const rows = Array.isArray(data) ? data : (data.data || data.items || []);
        if (!Array.isArray(rows) || rows.length === 0) break;

        faturalar.push(...rows);
        if (rows.length < pageSizeLogo) break;
      }

      const normalizeCariKey = (value) =>
        String(value || '')
          .toLocaleUpperCase('tr-TR')
          .replace(/\s+/g, ' ')
          .trim();

      const unique = new Map();

      faturalar.forEach(f => {
        const unvan = String(
          f.receiverTitle ||
          f.supplier ||
          ''
        ).trim();

        if (!unvan) return;

        const cariKey = normalizeCariKey(unvan);

        if (!unique.has(cariKey)) {
          unique.set(cariKey, {
            id: cariKey,
            firmId: cariKey,
            name: unvan,
            displayName: unvan,
            title: unvan,
            vknTckn: '',
            taxNumber: '',
          });
        }
      });

      const satisCarileri = Array.from(unique.values())
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'tr'));

      setCariler(satisCarileri);
      setTotalCount(satisCarileri.length);
      setPage(1);
    } catch (e) {
      logError(e);
    }
    setCariLoading(false);
  }, [logoConnected]);

  useEffect(() => { if (logoConnected) fetchCariler(); }, [fetchCariler, logoConnected]);

  // Eşleştir
  const handleEslestir = async (cari) => {
    const cariId = String(cari.id || cari.firmId || '');
    const tedarikciId = selectedTedarikci[cariId];
    if (!tedarikciId) return;
    try {
      const res = await fetch(`${API}/api/logo/eslestirmeler`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          logo_cari_id: cariId,
          logo_cari_unvan: cari.name || cari.displayName || cari.title || '',
          logo_vkn_tckn: cari.vknTckn || cari.taxNumber || '',
          tedarikci_id: parseInt(tedarikciId)
        })
      });
      if (res.ok) { showMsg('Cari eşleştirildi'); fetchEslestirmeler(); fetchCariler(); onRefresh(); setSelectedTedarikci(p => { const n = { ...p }; delete n[cariId]; return n; }); }
      else showMsg('Eşleştirme başarısız', 'error');
    } catch (e) { showMsg('Hata: ' + e.message, 'error'); }
  };

  // Kaldır
  const handleKaldir = async (id) => {
    if (!window.confirm('Bu eşleştirmeyi kaldırmak istediğinize emin misiniz?')) return;
    try {
      await fetch(`${API}/api/logo/eslestirmeler/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      showMsg('Eşleştirme kaldırıldı');
      fetchEslestirmeler(); onRefresh();
    } catch (e) { showMsg('Hata', 'error'); }
  };

  const eslesmisIds = new Set(eslestirmeler.map(e => String(e.logo_cari_id)));

  const filteredCariler = cariler.filter(c => {
    const cariId = String(c.id || c.firmId || '');
    const matchSearch = searchMatchAny(
      { name: c.name || c.displayName || c.title, vkn: c.vknTckn || c.taxNumber },
      searchTerm,
      ['name', 'vkn']
    );
    if (activeView === 'eslesmemis') return matchSearch && !eslesmisIds.has(cariId);
    if (activeView === 'eslesmis') return matchSearch && eslesmisIds.has(cariId);
    return matchSearch;
  });

  const eslesmemisSayi = cariler.filter(c => !eslesmisIds.has(String(c.id || c.firmId || ''))).length;
  const totalPages = Math.max(1, Math.ceil(filteredCariler.length / pageSize));
  const displayedCariler = filteredCariler.slice((page - 1) * pageSize, page * pageSize);

  const tedarikciOptions = tedarikciler.map(t => ({
    value: String(t.id),
    label: t.unvan || t.ad || `#${t.id}`
  }));

  return (
    <div>
      <p style={{ color: colors.textSecondary, marginBottom: 12, fontSize: 13 }}>
        Logo satış faturalarındaki alıcı carileri ile ERP tedarikçilerini eşleştirin. Liste yalnızca Logo GİDEN satış faturalarındaki alıcılardan oluşur.
      </p>

      {/* Mevcut Eşleştirmeler */}
      <div style={{ marginBottom: 24 }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: 14, color: colors.textPrimary }}>
          ✅ Mevcut Eşleştirmeler ({eslestirmeler.length})
        </h4>
        {eslLoading ? <p style={{ color: colors.textTertiary }}>Yükleniyor...</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt }}>
                <th style={thStyle}>Logo Cari</th>
                <th style={thStyle}>VKN/TCKN</th>
                <th style={thStyle}>ERP Tedarikçi</th>
                <th style={thStyle}>Tarih</th>
                <th style={{ ...thStyle, width: 60 }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {eslestirmeler.map(e => {
                const tName = tedarikciler.find(t => t.id === e.tedarikci_id);
                return (
                  <tr key={e.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={tdStyle}>{e.logo_cari_unvan || e.logo_cari_id}</td>
                    <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: 11 }}>{e.logo_vkn_tckn || '-'}</span></td>
                    <td style={tdStyle}><strong>{tName ? (tName.unvan || tName.ad) : `#${e.tedarikci_id}`}</strong></td>
                    <td style={tdStyle}>{e.eslestirme_tarihi ? new Date(e.eslestirme_tarihi).toLocaleDateString('tr-TR') : '-'}</td>
                    <td style={tdStyle}><button onClick={() => handleKaldir(e.id)} style={btnDel} title="Kaldır">🗑️</button></td>
                  </tr>
                );
              })}
              {eslestirmeler.length === 0 && <tr><td colSpan={5} style={emptyStyle}>Henüz eşleştirme yok</td></tr>}
            </tbody>
          </table>
        )}
      </div>

      {/* Logo Carileri */}
      {logoConnected === false && (
        <div style={{ padding: '12px 16px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>
          ⚠️ Logo İşbaşı bağlantısı kurulamadı. Aşağıda Logo carileri gösterilemiyor.
        </div>
      )}

      {logoConnected && (
        <div>
          <h4 style={{ margin: '0 0 10px 0', fontSize: 14, color: colors.textPrimary }}>
            🏢 Logo Satış Carileri
          </h4>

          {/* Filtre bar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Cari adı veya VKN ile ara..."
              style={{ padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 12, width: 240 }}
            />
            {['eslesmemis', 'eslesmis', 'tumu'].map(v => (
              <button
                key={v}
                onClick={() => setActiveView(v)}
                style={{
                  padding: '5px 12px', borderRadius: 4, fontSize: 11, fontWeight: 500, cursor: 'pointer',
                  border: activeView === v ? '1px solid #2c3e50' : `1px solid ${colors.border}`,
                  background: activeView === v ? '#2c3e50' : '#fff',
                  color: activeView === v ? '#fff' : '#555',
                }}
              >
                {v === 'eslesmemis' ? `Eşleşmemiş (${eslesmemisSayi})` : v === 'eslesmis' ? `Eşleşmiş (${eslestirmeler.length})` : 'Tümü'}
              </button>
            ))}
          </div>

          {cariLoading ? <p style={{ color: colors.textTertiary, padding: 20, textAlign: 'center' }}>Logo carileri yükleniyor...</p> : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: '#f0f4f8' }}>
                  <th style={thStyle}>Cari Adı</th>
                  <th style={thStyle}>VKN/TCKN</th>
                  <th style={thStyle}>Durum</th>
                  <th style={thStyle}>ERP Tedarikçi Seç</th>
                  <th style={{ ...thStyle, width: 80 }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {displayedCariler.map(c => {
                  const cariId = String(c.id || c.firmId || '');
                  const isMatched = eslesmisIds.has(cariId);
                  return (
                    <tr key={cariId} style={{ borderBottom: `1px solid ${colors.border}`, background: isMatched ? '#f0fff4' : 'white' }}>
                      <td style={tdStyle}>{c.name || c.displayName || c.title || '-'}</td>
                      <td style={tdStyle}><span style={{ fontFamily: 'monospace', fontSize: 11 }}>{c.vknTckn || c.taxNumber || '-'}</span></td>
                      <td style={tdStyle}>
                        {isMatched ? <span style={badgeOk}>Eşleşti</span> : <span style={badgeWarn}>Bekliyor</span>}
                      </td>
                      <td style={tdStyle}>
                        {!isMatched && (
                          <SearchableSelect
                            options={tedarikciOptions}
                            value={selectedTedarikci[cariId] || ''}
                            onChange={v => setSelectedTedarikci(p => ({ ...p, [cariId]: v }))}
                            placeholder="Tedarikçi seç..."
                            minWidth={200}
                          />
                        )}
                      </td>
                      <td style={tdStyle}>
                        {!isMatched && (
                          <button
                            onClick={() => handleEslestir(c)}
                            disabled={!selectedTedarikci[cariId]}
                            style={{
                              padding: '4px 12px', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                              background: selectedTedarikci[cariId] ? '#27ae60' : '#ccc',
                              color: '#fff',
                            }}
                          >
                            Eşleştir
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {displayedCariler.length === 0 && <tr><td colSpan={5} style={emptyStyle}>Sonuç bulunamadı</td></tr>}
              </tbody>
            </table>
          )}

          {/* Sayfalama */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 12 }}>
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtnStyle}>← Önceki</button>
              <span style={{ padding: '6px 12px', fontSize: 12, color: colors.textSecondary }}>{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtnStyle}>Sonraki →</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// LOGO ÜRÜN TAB
// ══════════════════════════════════════════════════════
function LogoUrunTab({ malzemeler, onRefresh, showMsg }) {
  const [eslestirmeler, setEslestirmeler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('eslesmemis');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/entegrasyon/eslestirmeler?dis_sistem=LOGO&dis_varlik_tipi=URUN`, { headers: getAuthHeaders() });
      if (res.ok) setEslestirmeler(await res.json());
    } catch (e) { logError(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGuncelle = async (id, erpHedefId) => {
    try {
      await fetch(`${API}/api/entegrasyon/eslestirmeler/${id}`, {
        method: 'PUT', headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ erp_hedef_id: parseInt(erpHedefId) }),
      });
      showMsg('Malzeme eşleştirildi'); fetchData(); onRefresh();
    } catch (e) { showMsg('Hata', 'error'); }
  };

  const handleSil = async (id) => {
    if (!window.confirm('Bu eşleştirmeyi kaldırmak istediğinize emin misiniz?')) return;
    try {
      await fetch(`${API}/api/entegrasyon/eslestirmeler/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      showMsg('Kaldırıldı'); fetchData(); onRefresh();
    } catch (e) { showMsg('Hata', 'error'); }
  };

  const malzemeOptions = malzemeler.map(m => ({ value: String(m.id), label: `${m.kod} — ${m.ad}` }));

  const eslesmisler = eslestirmeler.filter(e => e.erp_hedef_id);
  const eslesmemisler = eslestirmeler.filter(e => !e.erp_hedef_id);

  const filtered = useMemo(() => {
    const list = activeView === 'eslesmemis' ? eslesmemisler : activeView === 'eslesmis' ? eslesmisler : eslestirmeler;
    if (!searchTerm) return list;
    return list.filter(e => searchMatchAny(
      { adi: e.dis_varlik_adi, cari: (e.extras || {}).cari },
      searchTerm,
      ['adi', 'cari']
    ));
  }, [eslestirmeler, eslesmisler, eslesmemisler, activeView, searchTerm]);

  if (loading && eslestirmeler.length === 0) return <p style={{ textAlign: 'center', color: colors.textTertiary, padding: 30 }}>Yükleniyor...</p>;

  const tabBtnStyle = (isActive) => ({
    padding: '6px 16px', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600,
    background: isActive ? '#c62828' : '#f5f5f5', color: isActive ? 'white' : '#555',
  });

  return (
    <div>
      <p style={{ color: colors.textSecondary, marginBottom: 12, fontSize: 13 }}>
        E-faturalardan okunan ürünleri ERP malzemeleriyle eşleştirin. Eşleştirme yapıldığında sonraki senkronizasyonlarda stok otomatik girer.
      </p>

      <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button style={tabBtnStyle(activeView === 'eslesmemis')} onClick={() => setActiveView('eslesmemis')}>
          Eşleşmemiş ({eslesmemisler.length})
        </button>
        <button style={tabBtnStyle(activeView === 'eslesmis')} onClick={() => setActiveView('eslesmis')}>
          Eşleşmiş ({eslesmisler.length})
        </button>
        <button style={tabBtnStyle(activeView === 'tumu')} onClick={() => setActiveView('tumu')}>
          Tümü ({eslestirmeler.length})
        </button>
        <input
          value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          placeholder="Ürün adı veya cari ara..."
          style={{ marginLeft: 'auto', padding: '6px 12px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 12, width: 220 }}
        />
      </div>

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: colors.backgroundAlt }}>
            <th style={thStyle}>Ürün Adı</th>
            <th style={thStyle}>Kod</th>
            <th style={thStyle}>Cari / Kaynak</th>
            <th style={{ ...thStyle, minWidth: 200 }}>ERP Malzeme</th>
            <th style={thStyle}>Durum</th>
            <th style={{ ...thStyle, width: 50 }}>Sil</th>
          </tr>
        </thead>
        <tbody>
          {filtered.map(e => {
            const extras = e.extras || {};
            const eslesmisMi = !!e.erp_hedef_id;
            return (
              <tr key={e.id} style={{ borderBottom: `1px solid ${colors.border}`, background: eslesmisMi ? '#f0fdf4' : '#fff8f0' }}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{e.dis_varlik_adi || '-'}</td>
                <td style={tdStyle}>{extras.urun_kodu || extras.birim || '-'}</td>
                <td style={{ ...tdStyle, fontSize: 11, color: colors.textSecondary }}>{extras.cari || extras.fatura || '-'}</td>
                <td style={tdStyle}>
                  <SearchableSelect
                    options={malzemeOptions}
                    value={e.erp_hedef_id ? String(e.erp_hedef_id) : ''}
                    onChange={v => handleGuncelle(e.id, v)}
                    placeholder="Malzeme seç..."
                    minWidth={200}
                  />
                </td>
                <td style={tdStyle}>
                  {eslesmisMi
                    ? <span style={{ ...badgeInfo, background: '#e8f5e9', color: '#2e7d32' }}>{e.otomatik ? 'Oto' : 'Manuel'}</span>
                    : <span style={{ ...badgeGray, background: '#fff3e0', color: '#e65100' }}>Bekliyor</span>
                  }
                </td>
                <td style={tdStyle}><button onClick={() => handleSil(e.id)} style={btnDel} title="Kaldır">🗑️</button></td>
              </tr>
            );
          })}
          {filtered.length === 0 && (
            <tr><td colSpan={6} style={emptyStyle}>
              {eslestirmeler.length === 0
                ? 'Henüz ürün yok — Fatura Senkronizasyon sayfasından fatura senk edin'
                : 'Filtreyle eşleşen kayıt yok'}
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// MODPOS ÜRÜN TAB
// ══════════════════════════════════════════════════════
function ModPosTab({ receteler, malzemeler, onRefresh, showMsg }) {
  const [mappings, setMappings] = useState([]);
  const [unmapped, setUnmapped] = useState([]);
  const [ignored, setIgnored] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [loading, setLoading] = useState(true);
  const [newPosAdi, setNewPosAdi] = useState('');
  const [newTarget, setNewTarget] = useState('');

  const combinedOptions = useMemo(() => {
    const receteliMalzemeIds = new Set(
      receteler.filter(r => r.urun_malzeme_id).map(r => r.urun_malzeme_id)
    );
    const recOpts = receteler.map(r => ({ value: `recipe:${r.id}`, label: `[R] ${r.ad || r.recete_kodu}${r.tip ? ` (${r.tip})` : ''}` }));
    const malOpts = malzemeler
      .filter(m => !receteliMalzemeIds.has(m.id))
      .map(m => ({ value: `malzeme:${m.id}`, label: `[M] ${m.ad}${m.kod ? ` (${m.kod})` : ''}` }));
    return [...recOpts, ...malOpts];
  }, [receteler, malzemeler]);

  const parseTarget = (val) => {
    if (!val) return null;
    const s = String(val);
    if (s.startsWith('recipe:')) return { recipe_id: parseInt(s.split(':')[1]), malzeme_id: null };
    if (s.startsWith('malzeme:')) return { recipe_id: null, malzeme_id: parseInt(s.split(':')[1]) };
    return null;
  };

  const loadSuggestions = useCallback(async (unmappedList) => {
    if (!unmappedList || unmappedList.length === 0) return;
    const batch = unmappedList.slice(0, 20);
    const results = {};
    await Promise.allSettled(
      batch.map(async (u) => {
        try {
          const res = await fetch(`${API}/api/pos/suggest-mapping?pos_adi=${encodeURIComponent(u.urun_adi)}&limit=3`, { headers: getAuthHeaders() });
          if (res.ok) { const data = await res.json(); if (Array.isArray(data) && data.length > 0) results[u.urun_adi] = data; }
        } catch (_) { /* skip */ }
      })
    );
    setSuggestions(results);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [mapRes, unmRes, ignRes] = await Promise.all([
        fetch(`${API}/api/pos/mappings`, { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(`${API}/api/pos/unmapped`, { headers: getAuthHeaders() }).then(r => r.json()),
        fetch(`${API}/api/pos/ignored`, { headers: getAuthHeaders() }).then(r => r.json()).catch(() => []),
      ]);
      setMappings(Array.isArray(mapRes) ? mapRes : []);
      setIgnored(Array.isArray(ignRes) ? ignRes : []);
      const unmappedData = Array.isArray(unmRes) ? unmRes : [];
      setUnmapped(unmappedData);
      if (unmappedData.length > 0) loadSuggestions(unmappedData);
    } catch (e) { logError(e); }
    setLoading(false);
  }, [loadSuggestions]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async (posAdi, targetVal) => {
    const target = parseTarget(targetVal);
    if (!target || !posAdi) return;
    try {
      const res = await fetch(`${API}/api/pos/mappings`, {
        method: 'POST', headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ pos_urun_adi: posAdi.trim(), ...target })
      });
      if (res.ok) { showMsg('Eşleştirme kaydedildi'); loadData(); onRefresh(); }
      else showMsg('Kaydetme başarısız', 'error');
    } catch (e) { showMsg('Hata: ' + e.message, 'error'); }
  };

  // "Ürün değil / yok say": garson etiketi, masa iptali gibi operasyonel gürültüyü
  // eşleşmemiş listesinden + menü dashboard sayacından düşür (eşleştirme DEĞİL).
  const handleIgnore = async (posAdi) => {
    if (!posAdi) return;
    try {
      const res = await fetch(`${API}/api/pos/ignore`, {
        method: 'POST', headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ pos_urun_adi: posAdi })
      });
      if (res.ok) { showMsg(`"${posAdi}" yok sayıldı (ürün değil)`); loadData(); onRefresh(); }
      else { const err = await res.json().catch(() => ({})); showMsg('Yok sayılamadı: ' + (err.detail || res.status), 'error'); }
    } catch (e) { showMsg('Hata: ' + e.message, 'error'); }
  };

  const handleUnignore = async (id) => {
    try {
      const res = await fetch(`${API}/api/pos/ignore/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      if (res.ok) { showMsg('Geri alındı — tekrar eşleşmemiş listesinde'); loadData(); onRefresh(); }
      else showMsg('Geri alınamadı', 'error');
    } catch (e) { showMsg('Hata: ' + e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu eşleştirmeyi silmek istediğinize emin misiniz?')) return;
    try {
      await fetch(`${API}/api/pos/mappings/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
      showMsg('Eşleştirme silindi'); loadData(); onRefresh();
    } catch (e) { showMsg('Hata', 'error'); }
  };

  // Yeniden eşleştir: mapping'i değiştir + ürünün TÜM geçmiş satırlarını yeni
  // hedefe bağla + maliyet hesapla (POST mappings yalnız eşleşmemişleri düzeltir).
  const handleRemap = async (posAdi, targetVal) => {
    const target = parseTarget(targetVal);
    if (!target || !posAdi) return;
    if (!window.confirm(`"${posAdi}" ürününün TÜM geçmiş satırları yeni hedefe taşınacak ve maliyet yeniden hesaplanacak.\n\nDevam edilsin mi?`)) return;
    try {
      const res = await fetch(`${API}/api/pos/remap-product`, {
        method: 'POST', headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ pos_urun_adi: posAdi, ...target })
      });
      if (res.ok) {
        const body = await res.json();
        showMsg(`Yeniden eşleştirildi — ${body.lines_updated} satır, birim maliyet ${body.birim_maliyet}₺`);
        loadData(); onRefresh();
      } else {
        const err = await res.json().catch(() => ({}));
        showMsg('Yeniden eşleştirme başarısız: ' + (err.detail || res.status), 'error');
      }
    } catch (e) { showMsg('Hata: ' + e.message, 'error'); }
  };

  if (loading && mappings.length === 0 && unmapped.length === 0) return <p style={{ textAlign: 'center', color: colors.textTertiary, padding: 30 }}>Yükleniyor...</p>;

  return (
    <div>
      <p style={{ color: colors.textSecondary, marginBottom: 12, fontSize: 13 }}>
        ModPOS kasa terminalindeki ürün adları ile ERP reçetelerini/malzemelerini eşleştirin. POS satış raporları bu eşleştirme üzerinden işlenir.
      </p>

      {/* Yeni Eşleştirme Formu */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, padding: '12px 16px', background: colors.backgroundAlt, borderRadius: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          value={newPosAdi}
          onChange={e => setNewPosAdi(e.target.value)}
          placeholder="POS ürün adı..."
          style={{ padding: '6px 10px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 12, width: 200 }}
        />
        <SearchableSelect
          options={combinedOptions}
          value={newTarget}
          onChange={setNewTarget}
          placeholder="[R] Reçete / [M] Malzeme"
          minWidth={200}
        />
        <button
          onClick={() => { handleCreate(newPosAdi, newTarget); setNewPosAdi(''); setNewTarget(''); }}
          disabled={!newPosAdi.trim() || !newTarget}
          style={{ padding: '6px 16px', border: 'none', borderRadius: 4, background: '#27ae60', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: (!newPosAdi.trim() || !newTarget) ? 0.5 : 1 }}
        >
          + Ekle
        </button>
      </div>

      {/* Eşleşmemiş POS Ürünleri */}
      {unmapped.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: 14, color: '#e67e22' }}>
            ⚠️ Eşleşmemiş POS Ürünleri ({unmapped.length})
          </h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#fff8f0' }}>
                <th style={thStyle}>POS Ürün Adı</th>
                <th style={thStyle}>Satış Adedi</th>
                <th style={thStyle}>Öneri</th>
                <th style={thStyle}>ERP Hedef Seç</th>
                <th style={{ ...thStyle, width: 80 }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {unmapped.map((u, i) => {
                const sugs = suggestions[u.urun_adi] || [];
                return (
                  <UnmappedRow
                    key={u.urun_adi || i}
                    urun={u}
                    suggestions={sugs}
                    combinedOptions={combinedOptions}
                    onMap={(posAdi, target) => handleCreate(posAdi, target)}
                    onIgnore={handleIgnore}
                  />
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Yok sayılanlar (ürün değil — garson etiketi, masa iptali vb.) */}
      {ignored.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: 14, color: colors.textTertiary }}>
            🚫 Yok Sayılanlar — ürün değil ({ignored.length})
          </h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: colors.backgroundAlt }}>
                <th style={thStyle}>POS Ürün Adı</th>
                <th style={thStyle}>Satış Adedi</th>
                <th style={{ ...thStyle, width: 90 }}>İşlem</th>
              </tr>
            </thead>
            <tbody>
              {ignored.map(ig => (
                <tr key={ig.id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                  <td style={tdStyle}><span style={{ color: colors.textSecondary }}>{ig.pos_urun_adi}</span></td>
                  <td style={tdStyle}>{ig.count ?? '-'}</td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => handleUnignore(ig.id)}
                      style={{ padding: '4px 12px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: '#fff', color: colors.textPrimary }}
                      title="Tekrar eşleşmemiş listesine al"
                    >
                      Geri al
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mevcut Eşleştirmeler */}
      <h4 style={{ margin: '0 0 10px 0', fontSize: 14, color: colors.textPrimary }}>
        ✅ Mevcut Eşleştirmeler ({mappings.length})
      </h4>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ background: colors.backgroundAlt }}>
            <th style={thStyle}>POS Ürün Adı</th>
            <th style={thStyle}>Tür</th>
            <th style={thStyle}>ERP Hedef</th>
            <th style={thStyle}>Aktif</th>
            <th style={{ ...thStyle, width: 90 }}>İşlem</th>
          </tr>
        </thead>
        <tbody>
          {mappings.map(m => (
            <MappingRow key={m.id} m={m} combinedOptions={combinedOptions} onRemap={handleRemap} onDelete={handleDelete} />
          ))}
          {mappings.length === 0 && <tr><td colSpan={5} style={emptyStyle}>Kayıt bulunamadı</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

// Eşleşmemiş satır (öneri + seçim)
function UnmappedRow({ urun, suggestions, combinedOptions, onMap, onIgnore }) {
  const [selected, setSelected] = useState('');

  return (
    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
      <td style={tdStyle}><strong>{urun.urun_adi}</strong></td>
      <td style={tdStyle}>{urun.count || urun.satisAdet || '-'}</td>
      <td style={tdStyle}>
        {suggestions.length > 0 ? (
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            {suggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => {
                  const val = s.type === 'recipe' ? `recipe:${s.id}` : `malzeme:${s.id}`;
                  onMap(urun.urun_adi, val);
                }}
                style={{
                  padding: '2px 8px', borderRadius: 10, fontSize: 10, border: '1px solid #3498db',
                  background: '#ecf0f1', color: colors.textPrimary, cursor: 'pointer', fontWeight: 500,
                }}
                title={`Skor: ${s.score}`}
              >
                {s.type === 'recipe' ? '[R]' : '[M]'} {s.ad}
              </button>
            ))}
          </div>
        ) : (
          <span style={{ color: colors.textTertiary, fontSize: 11 }}>Öneri yok</span>
        )}
      </td>
      <td style={tdStyle}>
        <SearchableSelect
          options={combinedOptions}
          value={selected}
          onChange={setSelected}
          placeholder="Seçiniz..."
          minWidth={200}
        />
      </td>
      <td style={tdStyle}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={() => { if (selected) { onMap(urun.urun_adi, selected); setSelected(''); } }}
            disabled={!selected}
            style={{
              padding: '4px 12px', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer',
              background: selected ? '#27ae60' : '#ccc', color: '#fff',
            }}
          >
            Eşleştir
          </button>
          {onIgnore && (
            <button
              onClick={() => { if (window.confirm(`"${urun.urun_adi}" bir ürün değil mi (garson/iptal etiketi)?\n\nEşleşmemiş listesinden ve menü sayacından düşecek. Geri alınabilir.`)) onIgnore(urun.urun_adi); }}
              style={{
                padding: '4px 10px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 11, cursor: 'pointer',
                background: '#fff', color: colors.textTertiary, whiteSpace: 'nowrap',
              }}
              title="Ürün değil (garson etiketi / masa iptali gibi) — listeden gizle"
            >
              🚫 Yok say
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// Mevcut eşleştirme satırı — 🔄 Yeniden Eşleştir (remap) + 🗑️ Sil
function MappingRow({ m, combinedOptions, onRemap, onDelete }) {
  const [remapMode, setRemapMode] = useState(false);
  const [target, setTarget] = useState('');
  return (
    <tr style={{ borderBottom: `1px solid ${colors.border}` }}>
      <td style={tdStyle}><strong>{m.pos_urun_adi}</strong></td>
      <td style={tdStyle}>
        {m.recipe_id ? <span style={badgeInfo}>Reçete</span> : m.malzeme_id ? <span style={badgeOk}>Malzeme</span> : <span style={badgeGray}>-</span>}
      </td>
      <td style={tdStyle}>
        {remapMode ? (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <SearchableSelect options={combinedOptions} value={target} onChange={setTarget} placeholder="Yeni hedef seç..." minWidth={200} />
            <button
              onClick={() => { if (target) { onRemap(m.pos_urun_adi, target); setRemapMode(false); setTarget(''); } }}
              disabled={!target}
              style={{ padding: '4px 10px', border: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600, cursor: target ? 'pointer' : 'default', background: target ? '#27ae60' : '#ccc', color: '#fff' }}
            >
              Uygula
            </button>
            <button
              onClick={() => { setRemapMode(false); setTarget(''); }}
              style={{ padding: '4px 8px', border: `1px solid ${colors.border}`, borderRadius: 4, fontSize: 11, cursor: 'pointer', background: '#fff' }}
            >
              İptal
            </button>
          </div>
        ) : (m.recipe_ad || m.malzeme_ad || `#${m.recipe_id || m.malzeme_id}`)}
      </td>
      <td style={tdStyle}>
        {m.aktif !== false ? <span style={badgeOk}>Aktif</span> : <span style={badgeWarn}>Pasif</span>}
      </td>
      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
        <button onClick={() => setRemapMode(v => !v)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '4px 6px', opacity: 0.85 }} title="Yeniden Eşleştir">🔄</button>
        <button onClick={() => onDelete(m.id)} style={btnDel} title="Sil">🗑️</button>
      </td>
    </tr>
  );
}

// ── Yardımcı Bileşenler ──
function KpiCard({ label, value, icon, color }) {
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 8, padding: '14px 18px', borderLeft: `4px solid ${color}` }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{icon} {label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

// ── Stiller ──
const thStyle = { padding: '8px 10px', borderBottom: '2px solid #dee2e6', fontSize: 11, fontWeight: 600, color: '#555', textAlign: 'left' };
const tdStyle = { padding: '7px 10px', verticalAlign: 'middle' };
const emptyStyle = { padding: 30, textAlign: 'center', color: colors.textTertiary, fontSize: 13 };
const badgeOk = { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: '#d4edda', color: '#155724' };
const badgeWarn = { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: '#fff3cd', color: '#856404' };
const badgeInfo = { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: '#d1ecf1', color: '#0c5460' };
const badgeGray = { display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: '#e2e3e5', color: '#383d41' };
const btnDel = { background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, padding: '4px 6px', opacity: 0.7 };
const pageBtnStyle = { padding: '5px 12px', border: `1px solid ${colors.border}`, borderRadius: 4, background: colors.surface, cursor: 'pointer', fontSize: 12 };

export default EslestirmeMerkeziPage;
